import React, { useCallback, useEffect, useState } from "react";
import { Form, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";

import DiscardChangesPopup from "../component/sharedComponents/DiscardChangesPopup";
import { ReactComponent as WarningIcon } from "../assets/svg/warning-icon-popup.svg";
import { Routes } from "../constant/routes";
import { CLAIM_NOTE_TEXT_FIELD_NAME } from "../component/Patient/PatientApplications/PatientApplicationClaim/ClaimsNotesSection";
import { NOTE_CHARACTER_LIMIT } from "../component/sharedComponents/EditNotesUI";

export function RouterPrompt(props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { shouldTriggerPopup, popupDescription, noteForm, popupTitle, okText, cancelText, usePrimaryButton } = props;
  const claimNoteText = Form.useWatch(CLAIM_NOTE_TEXT_FIELD_NAME, noteForm) || "";
  const [modal, contextHolder] = Modal.useModal();

  const isClaimFormView =
    pathname.includes(`/${Routes.CLAIMS}/${Routes.NEW_CLAIM}`) ||
    (pathname.includes(`/${Routes.CLAIMS}/`) && pathname.includes(`/${Routes.EDIT_CLAIM}`)) ||
    (pathname.includes(`/${Routes.CLAIMS}/`) && pathname.includes(`/${Routes.DUPLICATE_CLAIM}`));

  const history = useHistory();
  const [showPrompt, setShowPrompt] = useState(false);
  const [currentPath, setCurrentPath] = useState("");

  useEffect(() => {
    const blocker = history.block((prompt) => {
      if (prompt.hash?.length == 0) {
        setCurrentPath(prompt.pathname);
        if (shouldTriggerPopup) {
          setShowPrompt(true);
          return false;
        }
        return true;
      }
    });
    return () => {
      blocker();
    };
  }, [history, shouldTriggerPopup]);

  const handleOK = useCallback(async () => {
    history.block(() => {});
    currentPath === Routes.SEARCH_RESULTS ? history.goBack() : history.push(currentPath);
  }, [currentPath]);

  const handleCancel = useCallback(async () => {
    setShowPrompt(false);

    // Set validation error for unsaved claim note text
    if (isClaimFormView && claimNoteText.trim()) {
      if (claimNoteText.trim().length > NOTE_CHARACTER_LIMIT) {
        noteForm.setFields([
          {
            name: CLAIM_NOTE_TEXT_FIELD_NAME,
            errors: [t("patientApplications.claims_tab.error_messages.notes_length_error")]
          }
        ]);
      } else {
        noteForm.setFields([
          {
            name: CLAIM_NOTE_TEXT_FIELD_NAME,
            errors: [t("patientApplications.claims_tab.error_message.unsaved_changes")]
          }
        ]);
      }
    }
  }, [currentPath, claimNoteText, noteForm, t, isClaimFormView]);

  useEffect(() => {
    if (showPrompt && isClaimFormView) {
      modal.confirm({
        centered: true,
        okText: t("yes_sure"),
        cancelText: t("back"),
        icon: <WarningIcon />,
        title: t("pop_up_messages.unsaved_changes"),
        content: popupDescription,
        onOk: handleOK,
        onCancel: handleCancel,
        okButtonProps: {
          id: "yes_sure_button"
        },
        cancelButtonProps: {
          id: "back_button"
        }
      });
    }
  }, [showPrompt, isClaimFormView, modal, t, handleOK, handleCancel]);

  return (
    <>
      {showPrompt && !isClaimFormView ? (
        <DiscardChangesPopup
          visible={showPrompt}
          onClickDiscard={handleOK}
          onClickCancel={handleCancel}
          popupTitle={popupTitle || t("managePatientDetails.cancel_modal_title")}
          popupDescription={popupDescription}
          okText={okText}
          cancelText={cancelText}
          usePrimaryButton={usePrimaryButton}
        />
      ) : null}
      {contextHolder}
    </>
  );
}
